<script setup>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Button} from "primevue";
import {computed, onMounted, ref, watch} from "vue";
import {formatPrice} from "@/utils/priceFormatter.js";
import {gregorianDate} from "@/utils/gregorianDate.js";
import StateColumn from "@/views/orders/Shared/StateColumn.vue";
import {useUserStore} from "@/stores/user.js";
import showAlert from "@/utils/showAlert.js";
import {showMessage} from "@/utils/showMessage.js";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {Icon} from "@iconify/vue";
import SortIcon from "@/components/datatable/sortIcon.vue";

const emit = defineEmits(['rowClick', 'add', 'showLogModal']);
const props = defineProps({
  loadingHotOrders: {
    type: Boolean,
    default: false
  },
  orderID: {
    type: Number,
    required: true
  },
  hotOrders: {
    type: Array,
    required: true
  }
})
const loading = ref(false)
const deleting = ref(false);
const userStore = useUserStore();
const ordersStore = useMainOrdersStore()
const users = computed(() => userStore.getAllUsers || [])
const hotOrders2 = ref(props.hotOrders)

const userMap = computed(() => {
  return users.value.reduce((map, user) => {
    map[user.id] = user.name || user.username || "Unknown"
    return map
  }, {})
})

const hotOrdersWithNames = computed(() => {
  return hotOrders2.value.map(order => {
    return {
      ...order,
      takerName: userMap.value[order.takerID] || "Unknown"
    }
  })
})

/* ---------- Initial fetch ---------- */
/*async function fetchInitialData() {
  loading.value = true
  // All Users
  await userStore.action_getAllUsers()
  loading.value = false
}*/

async function getHotOrders() {
  loading.value = true
  const result = await ordersStore.action_getHotOrders_currencySell({orderID: props.orderID})
  if (Array.isArray(result)) {
    hotOrders2.value = result
  }
  loading.value = false
}

function showLogModal(data) {
  emit('showLogModal', data)
}

function showUpdateModal({data}) {
  emit('rowClick', data)
}

function showAddModal({data}) {
  emit('add', data)
}

async function deleteHotOrder(data) {
  const ok = await showAlert(`You are deleting taker? (${data.title})`, 'Are you sure?', 'Yes', 'No')
  if (!ok) return

  // API call
  try {
    deleting.value = true
    const res = await ordersStore.action_removeHotOrder_currencySell({id: data.id})
    if (res === true) {
      showMessage('Taker deleted!', 'success')
      await getHotOrders();
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    deleting.value = false
  }
}

// هر بار prop تغییر کرد، localTitle هم آپدیت میشه
watch(() => props.hotOrders, (newVal) => {
  hotOrders2.value = newVal
})
/*onMounted(fetchInitialData)*/
</script>

<template>
  <div class="flex-1 flex flex-col gap-4 max-h-full min-h-[200px] w-full">
    <div class="flex-1 col-span-12 flex flex-col gap-1 text-xs 2xl:text-sm h-full">
      <div class="flex flex-row justify-between items-end">
        <div class="flex flex-row gap-4">
          <label for="takers">Takers:</label>
          <span>{{ hotOrdersWithNames.length }}</span>
        </div>
        <button class="py-1 px-2 bg-slate-800 text-xs" @click="showAddModal">Add</button>
      </div>
      <div
          class="flex-1 w-full overflow-hidden border border-slate-700 rounded-md flex flex-col">
        <div class="overflow-y-auto custom-scrollbar flex flex-col gap-2 h-full">
          <!-- Loading mode -->
          <!--          <div v-if="loadingHotOrders" class="flex items-center justify-center h-full animate-pulse bg-slate-700">
                      <Icon icon="eos-icons:bubble-loading" class="size-12"/>
                    </div>-->
          <DataTable
              :nullSortOrder="-1"
              :value="hotOrders2"
              scrollable
              scrollHeight="flex"
              tableStyle="min-width: 50rem"
              :virtualScrollerOptions="{ itemSize: 30 }"
              resizableColumns
              removableSort
              stripedRows
              class="text-xs w-full"
              size="small"
              row-hover
              @row-click="showUpdateModal"
              :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
          >
            <Column field="id" header="Id" :sortable="true"
                    style="width: 4%; min-width: 70px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder" />
              </template>
            </Column>
            <Column field="title" header="Title" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 15%; min-width: 200px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder" />
              </template>
            </Column>
            <Column field="description" header="Description" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 15%; min-width: 200px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder" />
              </template>
            </Column>
            <Column field="price" header="Price" dataType="numeric" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 8%; min-width: 80px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder" />
              </template>
              <template #body="slotProps">
                {{ slotProps.data.price || slotProps.data.price === 0 ? formatPrice(slotProps.data.price) + ' $' : '' }}
              </template>
            </Column>
            <Column field="takerName" header="Taker" :sortable="true">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder" />
              </template>
            </Column>
            <Column field="state" header="Status" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 8%; min-width: 80px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder" />
              </template>
              <template #body="slotProps">
                <StateColumn :state="slotProps.data.state"/>
              </template>
            </Column>
            <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 11%; min-width: 120px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder" />
              </template>
              <template #body="slotProps">
                <span class="block w-full max-w-max">
                  {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
                </span>
              </template>
            </Column>
            <Column field="creationTime" header="Creation Time" dataType="numeric" :sortable="true"
                    class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                    style="width: 10%; min-width: 120px; height: 30px">
              <template #sorticon="{ sortOrder }">
                <SortIcon :sortOrder="sortOrder" />
              </template>
              <template #body="slotProps">
                <span class="block w-full max-w-max">
                  {{ gregorianDate(slotProps.data.creationTime, 'DD MMM YYYY') }}
                </span>
              </template>
            </Column>
            <Column :exportable="false" style="width: 40px; height: 30px">
              <template #body="slotProps">
                <div class="flex flex-row gap-2 justify-end items-center">
                  <button @click.stop="deleteHotOrder(slotProps.data)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-trash2-icon lucide-trash-2">
                      <path d="M10 11v6"/>
                      <path d="M14 11v6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                      <path d="M3 6h18"/>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                  <button @click.stop="showLogModal(slotProps.data)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-history-icon lucide-history">
                      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                      <path d="M3 3v5h5"/>
                      <path d="M12 7v5l4 2"/>
                    </svg>
                  </button>
                </div>
              </template>
            </Column>
            <template #empty>
              <h1 class="py-2 text-center text-lg">No Data</h1>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.p-datatable-empty-message) {
  background: transparent;
}
</style>
